import React, { useRef, useEffect, useCallback } from 'react';
import './NeuralBackground.css';

const CONNECTION_DIST = 150;
const MOUSE_RADIUS = 220;
const CELL_SIZE = CONNECTION_DIST;

const NeuralBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const nodesRef = useRef([]);
  const animRef = useRef(null);
  const gridRef = useRef({});

  const getNodeCount = () => {
    if (typeof window === 'undefined') return 60;
    if (window.innerWidth < 640) return 30;
    if (window.innerWidth < 1024) return 50;
    return 70;
  };

  const createNodes = useCallback((width, height) => {
    const count = getNodeCount();
    const nodes = [];
    for (let i = 0; i < count; i++) {
      const isCyan = i < Math.floor(count * 0.15);
      const isGold = !isCyan && i < Math.floor(count * 0.22);
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        radius: Math.random() * 1.5 + 0.5,
        color: isCyan ? 'cyan' : isGold ? 'gold' : 'white',
        baseAlpha: Math.random() * 0.4 + 0.2,
      });
    }
    return nodes;
  }, []);

  // Spatial hash grid for fast neighbor lookups
  const buildGrid = useCallback((nodes) => {
    const grid = {};
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      const cx = Math.floor(n.x / CELL_SIZE);
      const cy = Math.floor(n.y / CELL_SIZE);
      const key = `${cx},${cy}`;
      if (!grid[key]) grid[key] = [];
      grid[key].push(i);
    }
    return grid;
  }, []);

  const getNeighborCells = (cx, cy) => {
    const cells = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        cells.push(`${cx + dx},${cy + dy}`);
      }
    }
    return cells;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Respect reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      canvas.style.display = 'none';
      return;
    }

    const ctx = canvas.getContext('2d');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2); // cap at 2x
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      nodesRef.current = createNodes(rect.width, rect.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const colorMap = {
      cyan: { r: 0, g: 242, b: 255 },
      gold: { r: 201, g: 168, b: 76 },
      white: { r: 255, g: 255, b: 255 },
    };

    const animate = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);

      const nodes = nodesRef.current;
      const mouse = mouseRef.current;

      // Update positions
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.x += n.vx;
        n.y += n.vy;

        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
        n.x = Math.max(0, Math.min(w, n.x));
        n.y = Math.max(0, Math.min(h, n.y));

        // Mouse attraction (gentle pull instead of repulsion — feels more alive)
        const dx = mouse.x - n.x;
        const dy = mouse.y - n.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < MOUSE_RADIUS && dist > 30) {
          const force = (MOUSE_RADIUS - dist) / MOUSE_RADIUS * 0.4;
          n.x += (dx / dist) * force;
          n.y += (dy / dist) * force;
        }
      }

      // Build spatial grid
      const grid = buildGrid(nodes);
      gridRef.current = grid;

      // Draw connections using spatial grid (much faster than O(n²))
      const drawnPairs = new Set();
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const cx = Math.floor(a.x / CELL_SIZE);
        const cy = Math.floor(a.y / CELL_SIZE);
        const neighbors = getNeighborCells(cx, cy);

        for (const key of neighbors) {
          const cell = grid[key];
          if (!cell) continue;
          for (const j of cell) {
            if (j <= i) continue;
            const pairKey = i < j ? `${i}-${j}` : `${j}-${i}`;
            if (drawnPairs.has(pairKey)) continue;
            drawnPairs.add(pairKey);

            const b = nodes[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < CONNECTION_DIST) {
              const alpha = (1 - dist / CONNECTION_DIST) * 0.12;

              const midX = (a.x + b.x) / 2;
              const midY = (a.y + b.y) / 2;
              const mouseDist = Math.sqrt(
                (midX - mouse.x) ** 2 + (midY - mouse.y) ** 2
              );
              const mi = Math.max(0, 1 - mouseDist / (MOUSE_RADIUS * 1.3));

              const r = Math.round(200 + mi * (-200));
              const g = Math.round(200 + mi * 42);
              const bVal = Math.round(200 + mi * 55);

              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.strokeStyle = `rgba(${r}, ${g}, ${bVal}, ${alpha + mi * 0.25})`;
              ctx.lineWidth = 0.4 + mi * 0.8;
              ctx.stroke();
            }
          }
        }
      }

      // Draw nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const c = colorMap[n.color];

        const dx = n.x - mouse.x;
        const dy = n.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const mouseGlow = dist < MOUSE_RADIUS ? (1 - dist / MOUSE_RADIUS) : 0;

        const alpha = n.baseAlpha + mouseGlow * 0.6;
        const r = n.radius + mouseGlow * 2.5;

        // Outer glow for nearby nodes
        if (mouseGlow > 0.3) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, r * 5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${mouseGlow * 0.06})`;
          ctx.fill();
        }

        // Node
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [createNodes, buildGrid]);

  return (
    <div className="neural-bg">
      <canvas ref={canvasRef} className="neural-bg__canvas" />
    </div>
  );
};

export default NeuralBackground;
